import { NavigationGuard, RouteConfig } from 'vue-router';
import { autoInvest } from '@store/modules/auto-invest';
import { defaultStarterInvestorGoalName } from '@src/constants';
import { getCurrentGoal } from '@api/investment-goals';
import { investmentEntity } from '@store/modules/investment-entity';
import { investmentGoals } from '@store/modules/investment-goals';
import { redirect } from '@src/utils/client';

const sameSessionCheck: NavigationGuard = async (to, from, next) => {
	if (investmentGoals.saveInvestorGoal.goalType === null) {
		next({ name: 'investment-goal-type' });
	} else {
		next();
	}
};

const sameAutoInvestSessionCheck: NavigationGuard = async (to, from, next) => {
	const isPlaidRedirect = to.query['oauth_state_id'];
	if (isPlaidRedirect && investmentEntity.investorGoalExists) {
		const goal = await getCurrentGoal();
		investmentGoals.UPDATE_SUGGESTED_AUTO_INVEST_AMOUNT(goal.monthlySuggestedAmount);

		await Promise.all([
			investmentEntity.storeBankAccounts(),
			autoInvest.getFrequenciesAndAmounts(),
			autoInvest.getAgreements()
		]);

		const plaidOauthId = to.query['oauth_state_id'];
		next({ query: { oauth_state_id: plaidOauthId } });
	} else if (!investmentEntity.investorGoalExists) {
		next({ name: 'investment-goal-type' });
	} else if (investmentEntity.autoInvestSchedule || !investmentGoals.autoInvestSuggestedAmount) {
		next({ name: 'investment-goal-edit' });
	} else {
		next();
	}
};

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const investmentGoalRoutes: Array<RouteConfig> = [
	{
		path: '/investment-goal',
		beforeEnter: async (to, from, next) => {
			next({ name: 'investment-goal-edit', replace: true });
		}
	},
	{
		path: `/investment-goal/create`,
		name: 'investment-goal',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storePositions();
			if (investmentEntity.eligibleForInvestorGoal) {
				next();
			} else {
				redirect('/account/overview');
			}
		},
		component: () =>
			import(/* webpackChunkName: "investment-goal" */ '@views/investment-goals/investment-goal-start.vue'),
		redirect: { name: 'investment-goal-type' },
		children: [
			{
				path: 'type',
				name: 'investment-goal-type',
				meta: {
					step: 1
				},
				beforeEnter: async (to, from, next) => {
					const isInvalidSource =
						investmentGoals.showPrefilledTemplateOption &&
						investmentGoals.saveInvestorGoal.goalName === defaultStarterInvestorGoalName;
					if (isInvalidSource) {
						investmentGoals.reset();
					}
					next();
				},
				component: () =>
					import(/* webpackChunkName: "investment-goal" */ '@views/investment-goals/investment-goal-type.vue')
			},
			{
				path: `time-horizon`,
				name: 'investment-goal-time-horizon',
				meta: {
					step: 2
				},
				beforeEnter: sameSessionCheck,
				component: () =>
					import(
						/* webpackChunkName: "investment-goal-time-horizon" */ '@views/investment-goals/investment-goal-time-horizon.vue'
					)
			},
			{
				path: `amount`,
				name: 'investment-goal-amount',
				meta: {
					step: 3
				},
				beforeEnter: async (to, from, next) => {
					if (investmentGoals.saveInvestorGoal.goalType === null) {
						next({ name: 'investment-goal-type' });
					} else if (investmentGoals.saveInvestorGoal.goalType !== 'INCOME') {
						await investmentGoals.setShowMonthlySuggestionAmounts();
					}
					next();
				},
				component: () =>
					import(
						/* webpackChunkName: "investment-goal-amount" */ '@views/investment-goals/investment-goal-amount.vue'
					)
			},
			{
				path: `confirm`,
				name: 'investment-goal-confirm',
				meta: {
					step: 4
				},
				beforeEnter: sameSessionCheck,
				component: () =>
					import(
						/* webpackChunkName: "investment-goal-confirm" */ '@views/investment-goals/investment-goal-confirm.vue'
					)
			},
			{
				path: `drip`,
				name: 'investment-goal-drip',
				meta: {
					step: 5
				},
				beforeEnter: sameSessionCheck,
				component: () =>
					import(
						/* webpackChunkName: "investment-goal-drip" */ '@views/investment-goals/investment-goal-drip.vue'
					)
			}
		]
	},
	{
		path: `/investment-goal/auto-invest`,
		name: 'investment-goal-auto-invest',
		beforeEnter: async (to, from, next) => {
			if (!investmentGoals.autoInvestSuggestedAmount) {
				next({ name: 'investment-goal' });
			} else {
				await investmentGoals.getAutoInvestSchedule();

				if (investmentGoals.autoInvestSchedule) {
					if (investmentGoals.isAutoInvestGreaterThanSuggestedAmount) {
						next({ name: 'investment-goal-success' });
					} else {
						next();
					}
				} else {
					next();
				}
			}
		},
		component: () =>
			import(
				/* webpackChunkName: "investment-goal-auto-invest" */ '@views/investment-goals/investment-goal-auto-invest.vue'
			)
	},
	{
		path: `/investment-goal/auto-invest-enable`,
		name: 'investment-goal-auto-invest-enable',
		beforeEnter: async (to, from, next) => {
			const isPlaidRedirect = to.query['oauth_state_id'];
			if (isPlaidRedirect) {
				const plaidOauthId = to.query['oauth_state_id'];
				next({ query: { oauth_state_id: plaidOauthId } });
			} else if (!investmentEntity.investorGoalExists) {
				next({ name: 'investment-goal-type' });
			} else if (investmentGoals.autoInvestSchedule) {
				next({ name: 'investment-goal-edit' });
			} else {
				await Promise.all([
					investmentEntity.storeBankAccounts(),
					autoInvest.getFrequenciesAndAmounts(),
					autoInvest.getAgreements()
				]);
				next();
			}
		},
		component: () =>
			import(
				/* webpackChunkName: "investment-goal-auto-invest-enable" */ '@views/investment-goals/investment-goal-auto-invest-enable.vue'
			),
		redirect: { name: 'investment-goal-auto-invest-new' },
		children: [
			{
				path: `new`,
				name: 'investment-goal-auto-invest-new',
				beforeEnter: sameAutoInvestSessionCheck,
				component: () =>
					import(
						/* webpackChunkName: "investment-goal-auto-invest-new" */ '@views/investment-goals/investment-goal-auto-invest-new.vue'
					)
			},
			{
				path: `agreements`,
				name: 'investment-goal-auto-invest-agreements',
				beforeEnter: sameAutoInvestSessionCheck,
				component: () =>
					import(
						/* webpackChunkName: "investment-goal-auto-invest-agrreements" */ '@views/investment-goals/investment-goal-auto-invest-agreements.vue'
					)
			},
			{
				path: `review`,
				name: 'investment-goal-auto-invest-review',
				beforeEnter: sameAutoInvestSessionCheck,
				component: () =>
					import(
						/* webpackChunkName: "investment-goal-auto-invest-review" */ '@views/investment-goals/investment-goal-auto-invest-review.vue'
					)
			}
		]
	},
	{
		path: `/investment-goal/auto-invest-edit`,
		name: 'investment-goal-auto-invest-edit',
		beforeEnter: async (to, from, next) => {
			if (!investmentEntity.investorGoalExists) {
				next({ name: 'investment-goal' });
			} else if (!investmentGoals.autoInvestSuggestedAmount) {
				next({ name: 'investment-goal-edit' });
			} else {
				investmentEntity.storeBankAccounts();
				next();
			}
		},
		component: () =>
			import(
				/* webpackChunkName: "investment-goal-auto-invest-edit" */ '@views/investment-goals/investment-goal-auto-invest-edit.vue'
			)
	},
	{
		path: `/investment-goal/success`,
		name: 'investment-goal-success',
		beforeEnter: async (to, from, next) => {
			if (!investmentEntity.investorGoalExists) {
				next({ name: 'investment-goal' });
			} else {
				next();
			}
		},
		component: () =>
			import(
				/* webpackChunkName: "investment-goal-success" */ '@views/investment-goals/investment-goal-success.vue'
			)
	},
	{
		path: `/investment-goal/view`,
		name: 'investment-goal-edit',
		beforeEnter: async (to, from, next) => {
			if (!investmentEntity.investorGoalExists) {
				next({ name: 'investment-goal' });
			} else {
				next();
			}
		},
		component: () =>
			import(/* webpackChunkName: "investment-goal" */ '@views/investment-goals/investment-goal-edit.vue')
	},
	{
		path: `/investment-goal/deleted/:goalId`,
		name: 'investment-goal-delete-success',
		component: () =>
			import(
				/* webpackChunkName: "investment-goal" */ '@views/investment-goals/investment-goal-delete-success.vue'
			),
		props: true
	},
	{
		path: '/investment-goal/recurring-investment',
		alias: '/investment-goal/update/recurring-investment',
		name: 'investment-goal-redirect',
		beforeEnter: async (to, from, next) => {
			next({ name: 'investment-goal-edit', replace: true });
		}
	}
];

export default investmentGoalRoutes;
